import React from 'react'
import {
  Grid,
  Button,
  Box,
  TextField,
  Typography,
  ButtonGroup,
} from '@mui/material'
import {
  AddCircleOutline as AddCircleOutlineIcon,
  AddCircle as AddCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'

import { Loading } from '../../common/components/Loading'

import { ArrowUpIcon } from '../../common/icons/ArrowUpIcon'
import { BellIcon } from '../../common/icons/BellIcon'
import { CalendarAddIcon } from '../../common/icons/CalendarAddIcon'
import { CalendarIcon } from '../../common/icons/CalendarIcon'
import { CaretUpIcon } from '../../common/icons/CaretUpIcon'
import { CloseIcon } from '../../common/icons/CloseIcon'
import { CogIcon } from '../../common/icons/CogIcon'
import { DashboardIcon } from '../../common/icons/DashboardIcon'
import { DoctorIcon } from '../../common/icons/DoctorIcon'
import { HelpIcon } from '../../common/icons/HelpIcon'
import { ListEditIcon } from '../../common/icons/ListEditIcon'
import { LocationIcon } from '../../common/icons/LocationIcon'
import { LockIcon } from '../../common/icons/LockIcon'
import { LogoutIcon } from '../../common/icons/LogoutIcon'
import { MenuIcon } from '../../common/icons/MenuIcon'
import { MessageIcon } from '../../common/icons/MessageIcon'
import { PhoneIcon } from '../../common/icons/PhoneIcon'
import { ProfileIcon } from '../../common/icons/ProfileIcon'
import { TimeIcon } from '../../common/icons/TimeIcon'
import { UsersIcon } from '../../common/icons/UsersIcon'
import { BoxWithLabel } from '../../common/components/BoxWithLabel'

const UiKit: React.FC<React.PropsWithChildren<unknown>> = () => {
  const icons = [
    { icon: <ArrowUpIcon />, name: 'ArrowUp' },
    { icon: <BellIcon />, name: 'Bell' },
    { icon: <CalendarAddIcon />, name: 'CalendarAdd' },
    { icon: <CalendarIcon />, name: 'Calendar' },
    { icon: <CaretUpIcon />, name: 'CaretUp' },
    { icon: <CloseIcon />, name: 'Close' },
    { icon: <CogIcon />, name: 'Cog' },
    { icon: <DashboardIcon />, name: 'Dashboard' },
    { icon: <DoctorIcon />, name: 'Doctor' },
    { icon: <HelpIcon />, name: 'Help' },
    { icon: <ListEditIcon />, name: 'ListEdit' },
    { icon: <LocationIcon />, name: 'Location' },
    { icon: <LockIcon />, name: 'Lock' },
    { icon: <LogoutIcon />, name: 'Logout' },
    { icon: <MenuIcon />, name: 'Menu' },
    { icon: <MessageIcon />, name: 'Message' },
    { icon: <PhoneIcon />, name: 'Phone' },
    { icon: <ProfileIcon />, name: 'Profile' },
    { icon: <TimeIcon />, name: 'Time' },
    { icon: <UsersIcon />, name: 'Users' },
  ]

  return (
    <>
      <Typography variant="h4">Colors</Typography>

      <Typography variant="body1">Primary</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.light" />
          {'Light'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.main" />
          {'Main'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.dark" />
          {'Dark'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.100" />
          {'100'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.200" />
          {'200'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.300" />
          {'300'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.400" />
          {'400'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="primary.500" />
          {'500'}
        </Grid>
      </Grid>

      <Typography variant="body1">Secondary</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="secondary.light" />
          {'Light'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="secondary.main" />
          {'Main'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="secondary.dark" />
          {'Dark'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="secondary.100" />
          {'100'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="secondary.200" />
          {'200'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="secondary.300" />
          {'300'}
        </Grid>
      </Grid>

      <Typography variant="body1">Error</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="error.light" />
          {'Light'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="error.main" />
          {'Main'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="error.dark" />
          {'Dark'}
        </Grid>
      </Grid>
      <Typography variant="body1">Success</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="success.light" />
          {'Light'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="success.main" />
          {'Main'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="success.dark" />
          {'Dark'}
        </Grid>
      </Grid>
      <Typography variant="body1">Warning</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="warning.light" />
          {'Light'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="warning.main" />
          {'Main'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="warning.dark" />
          {'Dark'}
        </Grid>
      </Grid>
      <Typography variant="body1">Info</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="info.light" />
          {'Light'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="info.main" />
          {'Main'}
        </Grid>
        <Grid item>
          <Box width={50} height={50} bgcolor="info.dark" />
          {'Dark'}
        </Grid>
      </Grid>
      <Typography variant="body1">Common</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <Box width={50} height={50} bgcolor="common.black" />
          {'Black'}
        </Grid>
        <Grid item>
          <Box
            width={50}
            height={50}
            bgcolor="common.white"
            border={'solid 1px black'}
          />
          {'White'}
        </Grid>
      </Grid>

      <Typography variant="h4">Typography</Typography>
      <Box mt={2} mb={10}>
        <Typography variant="h1">Headline 1</Typography>
        <Typography variant="h2">Headline 2</Typography>
        <Typography variant="h3">Headline 3</Typography>
        <Typography variant="h4">Headline 4</Typography>
        <Typography variant="h5">Headline 5</Typography>
        <Typography variant="h6">Headline 6</Typography>
        <Typography variant="body1">Body 1</Typography>
        <Typography variant="body2">Body 2</Typography>
        <Typography variant="subtitle1">Subtitle 1</Typography>
        <Typography variant="subtitle2">Subtitle 2</Typography>
        <Typography variant="button">Button</Typography>
        <br />
        <Typography variant="caption">Caption</Typography>
        <br />
        <Typography variant="overline">Overline</Typography>
        <br />
      </Box>

      <Typography variant="h4">Icons</Typography>
      <Box mt={2} mb={10}>
        {icons.map(({ icon, name }) => (
          <Box key={name} p={2} display="flex" alignItems="center">
            <Box mr={2}>{icon}</Box>
            <Typography>{name}</Typography>
          </Box>
        ))}
      </Box>

      <Typography variant="h4">Full Buttons</Typography>
      <Box mt={2} mb={10}>
        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button size="large">Large Button</Button>
          </Box>
          <Box ml={2}>
            <Button>Regular Button</Button>
          </Box>
          <Box ml={2}>
            <Button size="small">Small Button</Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button>Async Action</Button>
          </Box>
          <Box ml={2}>
            <Button disabled startIcon={<Loading inline size={12} />}>
              Loading
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button color="secondary" size="large">
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button color="secondary">Regular Button</Button>
          </Box>
          <Box ml={2}>
            <Button color="secondary" size="small">
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button disabled size="large">
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button disabled>Regular Button</Button>
          </Box>
          <Box ml={2}>
            <Button disabled size="small">
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button startIcon={<AddCircleOutlineIcon />} size="large">
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button startIcon={<AddCircleOutlineIcon />}>Regular Button</Button>
          </Box>
          <Box ml={2}>
            <Button startIcon={<AddCircleIcon />} size="small">
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button size="large">
              <Box display="flex" mx={-2} my={0.5}>
                <AddCircleOutlineIcon fontSize="large" />
              </Box>
            </Button>
          </Box>
          <Box ml={2}>
            <Button>
              <Box display="flex" mx={-2} my={0.5}>
                <AddCircleOutlineIcon />
              </Box>
            </Button>
          </Box>
          <Box ml={2}>
            <Button size="small">
              <Box display="flex" mx={-2} my={0.5}>
                <AddCircleIcon fontSize="small" />
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>

      <Typography variant="h4">Outlined Buttons</Typography>
      <Box mt={2} mb={10}>
        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button variant="outlined" size="large">
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined">Regular Button</Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" size="small">
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button variant="outlined" color="secondary" size="large">
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" color="secondary">
              Regular Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" color="secondary" size="small">
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button variant="outlined" disabled size="large">
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" disabled>
              Regular Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" disabled size="small">
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              size="large"
            >
              Large Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon />}>
              Regular Button
            </Button>
          </Box>
          <Box ml={2}>
            <Button
              variant="outlined"
              startIcon={<AddCircleIcon />}
              size="small"
            >
              Small Button
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <Button variant="outlined" size="large">
              <AddCircleOutlineIcon sx={{ display: 'flex', mx: -2, my: 0.5 }} />
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined">
              <AddCircleOutlineIcon
                fontSize="small"
                sx={{ display: 'flex', mx: -2, my: 0.5 }}
              />
            </Button>
          </Box>
          <Box ml={2}>
            <Button variant="outlined" size="small">
              <AddCircleIcon
                fontSize="small"
                sx={{ display: 'flex', mx: -2, my: 0.5 }}
              />
            </Button>
          </Box>
        </Box>

        <Box mt={2} display="flex">
          <Box ml={2}>
            <ButtonGroup color="primary" variant="outlined" size="small">
              <Button>
                <ChevronLeftIcon
                  fontSize="small"
                  sx={{ display: 'flex', mx: -2, my: 0.5 }}
                />
              </Button>
              <Button>
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ display: 'flex', mx: -2, my: 0.5 }}
                />
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      <Typography variant="h4">Text input</Typography>

      <BoxWithLabel label="Medium input">
        <TextField
          size="medium"
          placeholder="Placeholder text"
          helperText="Helper text"
        />
      </BoxWithLabel>

      <BoxWithLabel label="Small input">
        <TextField
          size="small"
          placeholder="Placeholder text"
          helperText="Helper text"
        />
      </BoxWithLabel>
    </>
  )
}

export { UiKit }
